import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SearchFilters = ({ filters, onFiltersChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const propertyTypes = [
    { value: '1bhk', label: '1 BHK' },
    { value: '2bhk', label: '2 BHK' },
    { value: '3bhk', label: '3 BHK' },
    { value: '4bhk', label: '4+ BHK' },
    { value: 'studio', label: 'Studio' },
    { value: 'villa', label: 'Villa' },
    { value: 'plot', label: 'Plot' }
  ];

  const amenities = [
    { value: 'parking', label: 'Parking' },
    { value: 'gym', label: 'Gym' },
    { value: 'swimming_pool', label: 'Swimming Pool' },
    { value: 'security', label: '24/7 Security' },
    { value: 'power_backup', label: 'Power Backup' },
    { value: 'elevator', label: 'Elevator' },
    { value: 'garden', label: 'Garden' },
    { value: 'clubhouse', label: 'Clubhouse' }
  ];

  const proximityOptions = [
    { value: 'metro', label: 'Metro Station' },
    { value: 'school', label: 'Schools' },
    { value: 'hospital', label: 'Hospitals' },
    { value: 'mall', label: 'Shopping Mall' },
    { value: 'airport', label: 'Airport' },
    { value: 'office', label: 'IT Parks' }
  ];

  const locationOptions = [
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi', label: 'Delhi' },
    { value: 'pune', label: 'Pune' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'chennai', label: 'Chennai' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleArrayFilterChange = (key, value, checked) => {
    const currentArray = filters[key] || [];
    if (checked) {
      handleFilterChange(key, [...currentArray, value]);
    } else {
      handleFilterChange(key, currentArray.filter(item => item !== value));
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-property-card">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="font-semibold text-text-primary">Filters</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            iconName="X"
            iconPosition="left"
          >
            Clear All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {isExpanded ? "Less" : "More"}
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Location Search */}
        <div>
          <Select
            label="Location"
            placeholder="Select city"
            options={locationOptions}
            value={filters.location || ''}
            onChange={(value) => handleFilterChange('location', value)}
            searchable
            className="mb-4"
          />
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Price Range (₹)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="Min Price"
              value={filters.minPrice || ''}
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
            />
            <Input
              type="number"
              placeholder="Max Price"
              value={filters.maxPrice || ''}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-text-secondary">
            <span>₹10,000</span>
            <span>₹5,00,000+</span>
          </div>
        </div>

        {/* Property Type */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Property Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {propertyTypes.map((type) => (
              <Checkbox
                key={type.value}
                label={type.label}
                checked={(filters.propertyTypes || []).includes(type.value)}
                onChange={(e) => handleArrayFilterChange('propertyTypes', type.value, e.target.checked)}
              />
            ))}
          </div>
        </div>

        {/* Expanded Filters */}
        {isExpanded && (
          <>
            {/* Amenities */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Amenities
              </label>
              <div className="grid grid-cols-2 gap-2">
                {amenities.map((amenity) => (
                  <Checkbox
                    key={amenity.value}
                    label={amenity.label}
                    checked={(filters.amenities || []).includes(amenity.value)}
                    onChange={(e) => handleArrayFilterChange('amenities', amenity.value, e.target.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Proximity */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Near By
              </label>
              <div className="grid grid-cols-2 gap-2">
                {proximityOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    checked={(filters.proximity || []).includes(option.value)}
                    onChange={(e) => handleArrayFilterChange('proximity', option.value, e.target.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Radius */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Search Radius
              </label>
              <Select
                placeholder="Select radius"
                options={[
                  { value: '1', label: 'Within 1 km' },
                  { value: '3', label: 'Within 3 km' },
                  { value: '5', label: 'Within 5 km' },
                  { value: '10', label: 'Within 10 km' }
                ]}
                value={filters.radius || '5'}
                onChange={(value) => handleFilterChange('radius', value)}
              />
            </div>

            {/* Furnishing */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Furnishing
              </label>
              <div className="space-y-2">
                {['Fully Furnished', 'Semi Furnished', 'Unfurnished'].map((furnishing) => (
                  <Checkbox
                    key={furnishing}
                    label={furnishing}
                    checked={(filters.furnishing || []).includes(furnishing)}
                    onChange={(e) => handleArrayFilterChange('furnishing', furnishing, e.target.checked)}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Apply Filters Button */}
        <Button
          variant="default"
          fullWidth
          iconName="Search"
          iconPosition="left"
          className="mt-6"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

export default SearchFilters;